import { IRRInputs, DerivedInputs, CashflowRow, IRRResult } from '../types/irr';

export function calculateDerivedInputs(inputs: IRRInputs): DerivedInputs {
  const gstRateDecimal = inputs.gstRate / 100;
  const basePrice = inputs.totalAssetCost / (1 + gstRateDecimal);
  const gstAmount = basePrice * gstRateDecimal;
  const securityDepositAmount = inputs.programLimit * (inputs.securityDepositPercent / 100);
  const purchasePriceAmount = inputs.programLimit * (inputs.purchasePricePercent / 100);
  const refundSecurityDepositAmount = inputs.programLimit * (inputs.refundSecurityDepositPercent / 100);
  const processingFeesAmount = inputs.programLimit * (inputs.processingFeesPercent / 100);
  const ptpmPostGstPassback = inputs.ptpmConsideration - inputs.gstPassback;
  const periodicRental = inputs.programLimit * ptpmPostGstPassback / 1000;
  const monthlyRental = inputs.paymentFrequency === 'quarterly' ? periodicRental / 3 : periodicRental;

  return {
    basePrice, gstAmount, securityDepositAmount, purchasePriceAmount,
    refundSecurityDepositAmount, processingFeesAmount, ptpmPostGstPassback,
    monthlyRental, periodicRental,
  };
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  const day = result.getDate();
  result.setMonth(result.getMonth() + months);
  if (result.getDate() !== day) result.setDate(0);
  return result;
}

export function generateCashflowSchedule(inputs: IRRInputs, derived: DerivedInputs): CashflowRow[] {
  const schedule: CashflowRow[] = [];
  const commencementDate = new Date(inputs.commencementDate);
  const corporateTaxRate = inputs.corporateTaxBenefit / 100;
  const periodIncrement = inputs.paymentFrequency === 'quarterly' ? 3 : 1;
  const totalPeriods = inputs.paymentFrequency === 'quarterly'
    ? Math.floor(inputs.tenureMonths / 3)
    : inputs.tenureMonths;

  const initialOutflow = -inputs.programLimit + derived.processingFeesAmount;
  schedule.push({
    rowIndex: 0, rentalDate: new Date(commencementDate),
    rentalExclGst: initialOutflow, additionalTaxSaving: 0,
    actualCostIncurred: initialOutflow, description: 'Initial Investment (Net of PF)',
  });

  const advanceRentalPeriods = inputs.advanceRentalPeriods || 0;
  const advanceRentalAmount = advanceRentalPeriods > 0 ? derived.periodicRental * advanceRentalPeriods : 0;
  const firstRentalWithSD = derived.periodicRental + derived.securityDepositAmount + advanceRentalAmount;
  const firstTaxSaving = (derived.periodicRental + advanceRentalAmount) * corporateTaxRate;
  schedule.push({
    rowIndex: 1, rentalDate: new Date(commencementDate),
    rentalExclGst: firstRentalWithSD, additionalTaxSaving: firstTaxSaving,
    actualCostIncurred: firstRentalWithSD - firstTaxSaving,
    description: advanceRentalPeriods > 0
      ? `First ${inputs.paymentFrequency === 'quarterly' ? 'Quarterly' : 'Monthly'} Rental + SD + Advance Rental`
      : `First ${inputs.paymentFrequency === 'quarterly' ? 'Quarterly' : 'Monthly'} Rental + SD`,
  });

  for (let i = 2; i < totalPeriods; i++) {
    const monthsFromStart = (i - 1) * periodIncrement;
    const rentalDate = addMonths(commencementDate, monthsFromStart);
    const taxSaving = derived.periodicRental * corporateTaxRate;
    schedule.push({
      rowIndex: i, rentalDate, rentalExclGst: derived.periodicRental,
      additionalTaxSaving: taxSaving, actualCostIncurred: derived.periodicRental - taxSaving,
    });
  }

  const finalMonthsFromStart = (totalPeriods - 1) * periodIncrement;
  const finalDate = addMonths(commencementDate, finalMonthsFromStart);
  const finalRental = derived.periodicRental - derived.refundSecurityDepositAmount;
  const finalTaxSaving = derived.periodicRental * corporateTaxRate;
  schedule.push({
    rowIndex: totalPeriods, rentalDate: finalDate,
    rentalExclGst: finalRental, additionalTaxSaving: finalTaxSaving,
    actualCostIncurred: derived.periodicRental - finalTaxSaving - derived.refundSecurityDepositAmount,
    description: 'Final Rental - SD Refund',
  });

  return schedule;
}

export function calculateIRRPeriodic(cashflows: number[], guess: number = 0.01): number {
  if (cashflows.length < 2) throw new Error('Invalid cashflow data');
  let rate = guess;
  const maxIterations = 100;
  const tolerance = 1e-10;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    let npv = 0, derivative = 0;
    for (let i = 0; i < cashflows.length; i++) {
      const factor = Math.pow(1 + rate, i);
      npv += cashflows[i] / factor;
      derivative -= i * cashflows[i] / (factor * (1 + rate));
    }
    if (Math.abs(npv) < tolerance) return rate;
    if (Math.abs(derivative) < tolerance) break;
    const newRate = rate - npv / derivative;
    if (newRate > 1) rate = 1;
    else if (newRate < -0.99) rate = -0.99;
    else rate = newRate;
  }
  return rate;
}

export function calculateIRR(inputs: IRRInputs): IRRResult {
  const derived = calculateDerivedInputs(inputs);
  const cashflowSchedule = generateCashflowSchedule(inputs, derived);
  const rentalExclGstValues = cashflowSchedule.map(row => row.rentalExclGst);
  const actualCostValues = cashflowSchedule.map(row => row.actualCostIncurred);
  const periodicIrrRental = calculateIRRPeriodic(rentalExclGstValues);
  const periodicIrrActual = calculateIRRPeriodic(actualCostValues);
  const annualizeMultiplier = inputs.paymentFrequency === 'quarterly' ? 4 : 12;
  const irrRentalExclGst = periodicIrrRental * annualizeMultiplier;
  const irrActualCost = periodicIrrActual * annualizeMultiplier;

  return { irrRentalExclGst, irrActualCost, cashflowSchedule, derivedInputs: derived };
}

export function formatCurrency(value: number): string {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const formatter = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  return sign + '₹' + formatter.format(absValue);
}

export function formatPercentage(value: number, decimals: number = 2): string {
  return (value * 100).toFixed(decimals) + '%';
}
